import { Link } from '@tanstack/react-router'
import { Menu, PanelLeft, PanelLeftClose } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'

import { BranchSelector } from '@/components/app-shell/branch-selector'
import { BusinessDate } from '@/components/app-shell/business-date'
import { HeaderContextMenu } from '@/components/app-shell/header-context-menu'
import { NavigationDrawer } from '@/components/app-shell/navigation-drawer'
import { SidebarNav } from '@/components/app-shell/sidebar-nav'
import { UserMenu } from '@/components/app-shell/user-menu'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { env } from '@/lib/env'
import { useIsAtLeast } from '@/lib/use-viewport'
import { cn } from '@/lib/utils'

const SIDEBAR_STORAGE_KEY = 'microcredit.sidebar-collapsed'

const readCollapsed = () => {
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * The frame every signed-in screen renders inside: a dark navigation rail, a top bar carrying the
 * operating context — branch and business date — and a scrolling work area.
 *
 * The branch and the business date sit in the top bar rather than on each screen because they
 * qualify everything below them. A figure means one thing in Kaneshie on an open day and
 * something else in Madina after close, and an operator should never have to go looking for which
 * they are in.
 *
 * Below `lg` the rail becomes a drawer and the two context controls fold into one compact menu.
 * At `lg` and above nothing changes: the collapsible column is the layout this application is
 * built for, and the narrow arrangement is the exception it makes rather than the shape it takes.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const isWide = useIsAtLeast('lg')

  // The rail reappears at `lg`, and a drawer left standing over it would be a second copy of the
  // same navigation with a backdrop over the work behind it.
  useEffect(() => {
    if (isWide) {
      setDrawerOpen(false)
    }
  }, [isWide])

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current
      try {
        window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next))
      } catch {
        // Storage refused; the preference simply does not survive a reload.
      }
      return next
    })
  }

  return (
    <div className="flex h-full bg-canvas">
      <aside
        className={cn(
          'hidden shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-150 lg:flex',
          collapsed ? 'w-14' : 'w-60',
        )}
      >
        <div className={cn('flex h-12 items-center gap-2 px-3', collapsed && 'justify-center px-0')}>
          <Link to="/" className="flex items-center gap-2 overflow-hidden" aria-label="Dashboard">
            <span
              className="flex size-7 shrink-0 items-center justify-center rounded-md bg-brand-fill text-on-brand-fill"
              aria-hidden
            >
              <svg viewBox="0 0 32 32" className="size-4.5" fill="none" stroke="currentColor" strokeWidth={3}>
                <path d="M8 22V10.5l8 6 8-6V22" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            {!collapsed ? (
              <span className="truncate text-sm font-semibold text-sidebar-text">Microcredit</span>
            ) : null}
          </Link>
        </div>

        <Separator className="bg-sidebar-border" />

        <SidebarNav collapsed={collapsed} />

        <div className={cn('border-t border-sidebar-border p-2', collapsed && 'flex justify-center')}>
          <button
            type="button"
            onClick={toggleCollapsed}
            className="flex items-center gap-2 rounded-control px-2 py-1.5 text-xs text-sidebar-muted transition-colors hover:bg-sidebar-hover hover:text-sidebar-text"
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            {collapsed ? <PanelLeft className="size-4" /> : <PanelLeftClose className="size-4" />}
            {!collapsed ? <span>Collapse</span> : null}
          </button>
        </div>
      </aside>

      <NavigationDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        returnFocusTo={menuButtonRef}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="inset-safe-x inset-safe-top shrink-0 border-b border-border-subtle bg-surface">
          <div className="flex h-12 items-center gap-2 px-2 sm:px-3 lg:h-12 lg:gap-3 lg:px-4">
            <Button
              ref={menuButtonRef}
              variant="ghost"
              size="icon"
              className="lg:hidden"
              aria-label="Open navigation"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
            >
              <Menu />
            </Button>

            {isWide ? (
              <>
                <BranchSelector />
                <Separator orientation="vertical" className="h-5" />
                <BusinessDate />
              </>
            ) : (
              <HeaderContextMenu />
            )}

            <div className="ml-auto flex shrink-0 items-center gap-1 lg:gap-2">
              {env.environmentLabel && !env.isProduction ? (
                <Badge tone="warning">{env.environmentLabel}</Badge>
              ) : null}
              <UserMenu />
            </div>
          </div>
        </header>

        <main className="inset-safe-x min-w-0 flex-1 overflow-y-auto">
          <div className="inset-safe-bottom mx-auto w-full max-w-[110rem] space-y-4 p-3 sm:p-4">{children}</div>
        </main>
      </div>
    </div>
  )
}
